import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Landmark, Wallet, Lock, ShieldCheck, ArrowLeft, Loader2 } from "lucide-react";

const methods = [
  { id: "card", name: "Credit / Debit Card", icon: CreditCard, badge: "Instant", desc: "Visa, Mastercard, Amex — via Stripe secure checkout." },
  { id: "paypal", name: "PayPal", icon: Wallet, badge: "Instant", desc: "Pay with your PayPal account or card." },
  { id: "bank_transfer", name: "Bank Transfer (SEPA/SWIFT)", icon: Landmark, badge: "Manual", desc: "Download unlocked after we verify your transfer." },
];

export default function Checkout() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "", city: "", postal_code: "", country: "Portugal" });

  useEffect(() => { api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => toast.error("Product not found")); }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const valid = form.full_name && form.email && form.address && form.city && form.postal_code && form.country;

  const submit = async () => {
    if (!valid) { toast.error("Please fill all required fields."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        product_id: id, payment_method: method, customer: form, origin_url: window.location.origin,
      });
      if (data.type === "redirect") { window.location.href = data.checkout_url; return; }
      if (data.type === "bank_transfer") { nav(`/payment/success?order=${data.order_number}`); return; }
      if (data.type === "pending") { toast.info(data.message); nav(`/payment/success?order=${data.order_number}`); return; }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (!p) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-400">Loading…</div>;

  const field = (k, label, opts = {}) => (
    <div className={opts.full ? "sm:col-span-2" : ""}>
      <label className="block text-xs font-medium text-slate-400 mb-1.5">{label}{opts.optional ? " (optional)" : " *"}</label>
      <input value={form[k]} onChange={set(k)} type={opts.type || "text"} placeholder={opts.ph || ""}
        data-testid={`checkout-${k.replace(/_/g, "-")}-input`}
        className="w-full bg-[#0A0E1A] border border-[#1E2F4D] rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#2563EB]" />
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-slate-400 hover:text-cyan-400 text-sm mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">Checkout</h1>
            <p className="text-slate-400 text-sm mt-1">No account required — just your details.</p>
          </div>

          <div className="tactical-card p-6">
            <h2 className="font-display font-semibold text-white mb-5">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {field("full_name", "Full name", { full: true, ph: "John Smith" })}
              {field("email", "Email", { type: "email", ph: "john@example.com" })}
              {field("phone", "Phone", { optional: true, ph: "+351 …" })}
              {field("address", "Address", { full: true, ph: "Street and number" })}
              {field("city", "City")}
              {field("postal_code", "Postal code")}
              {field("country", "Country", { full: true })}
            </div>
          </div>

          <div className="tactical-card p-6">
            <h2 className="font-display font-semibold text-white mb-5">Payment method</h2>
            <div className="space-y-3">
              {methods.map((m) => (
                <button key={m.id} onClick={() => setMethod(m.id)} data-testid={`pay-method-${m.id}`}
                  className={`w-full text-left flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                    method === m.id ? "border-[#2563EB] bg-[#172642]" : "border-[#1E2F4D] bg-[#0A0E1A] hover:border-slate-600"
                  }`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${method === m.id ? "bg-[#2563EB]" : "bg-[#111C30]"}`}>
                    <m.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white text-sm">{m.name}</span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/30">{m.badge}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{m.desc}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 ${method === m.id ? "border-[#2563EB] bg-[#2563EB]" : "border-slate-600"}`} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* SUMMARY */}
        <div className="lg:col-span-2">
          <div className="tactical-card p-6 sticky top-24">
            <h2 className="font-display font-semibold text-white mb-5">Order summary</h2>
            <div className="space-y-3 text-sm">
              <Row l="Product" v={p.title} />
              <Row l="Price" v={euro(p.price)} />
              <Row l="Customer" v={form.full_name || "—"} />
              <Row l="Email" v={form.email || "—"} />
              <Row l="Address" v={form.address ? `${form.address}, ${form.city}` : "—"} />
              <Row l="Payment" v={methods.find((m) => m.id === method)?.name} />
            </div>
            <div className="flex items-center justify-between mt-5 pt-5 border-t border-[#1E2F4D]">
              <span className="text-slate-300 font-medium">Total</span>
              <span className="font-display text-2xl font-extrabold text-white" data-testid="checkout-total">{euro(p.price)}</span>
            </div>
            <button onClick={submit} disabled={loading}
              className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-full"
              data-testid="place-order-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
              {loading ? "Processing…" : "Confirm & Pay"}
            </button>
            <p className="text-xs text-slate-500 mt-4 flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" /> Payments are processed securely. We never store card details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-slate-500">{l}</span>
      <span className="text-slate-200 text-right break-words max-w-[60%]">{v}</span>
    </div>
  );
}
